import React, { useState, useEffect } from "react"
import CreatableSelect from "react-select/creatable"
import { pipeline, env } from "@xenova/transformers"
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts"

env.allowLocalModels = false

const NEON_PRIMARY = "#00FF7A"

const components = {
  DropdownIndicator: null,
}

const createOption = (label) => ({
  label,
  value: label,
})

const sampleClassificationData = [
  {
    inputText: "The protein molecules folded into complex shapes.",
    labels: ["Science", "Technology", "Art", "History"], // Correct: Science, Others: Confounding
  },
  {
    inputText: "The guitarist played a melancholic tune on his instrument.",
    labels: ["Music", "Sports", "Travel", "Politics"], // Correct: Music, Others: Confounding
  },
  {
    inputText: "She baked a delicious chocolate cake from scratch.",
    labels: ["Food", "Fashion", "Business", "Education"], // Correct: Food, Others: Confounding
  },
  {
    inputText: "The athlete trained rigorously for the upcoming marathon.",
    labels: ["Sports", "Medicine", "Technology", "Entertainment"], // Correct: Sports, Others: Confounding
  },
  {
    inputText:
      "The Louvre museum houses a vast collection of art masterpieces.",
    labels: ["Art", "History", "Science", "Travel"], // Correct: Art, Others: Confounding
  },
  {
    inputText: "The company announced a new line of eco-friendly products.",
    labels: ["Business", "Technology", "Fashion", "Environment"], // Correct: Business, Others: Confounding
  },
  {
    inputText: "The documentary explored the life of a marine biologist.",
    labels: ["Science", "Education", "Entertainment", "Travel"], // Correct: Science, Others: Confounding
  },
  {
    inputText: "The upcoming election has generated a lot of political debate.",
    labels: ["Politics", "Business", "Technology", "Social Media"], // Correct: Politics, Others: Confounding
  },
  {
    inputText:
      "The fashion show showcased the latest trends in clothing design.",
    labels: ["Fashion", "Entertainment", "Art", "Business"], // Correct: Fashion, Others: Confounding
  },
  {
    inputText:
      "The hospital implemented new protocols to improve patient care.",
    labels: ["Medicine", "Education", "Technology", "Social Media"], // Correct: Medicine, Others: Confounding
  },
]

const TextClassification = () => {
  const [inputValue, setInputValue] = useState("")
  const [classificationLabels, setClassificationLabels] = useState([])
  const [classificationText, setClassificationText] = useState("")
  const [classificationResult, setClassificationResult] = useState(null)
  const [classifier, setClassifier] = useState(null)

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "rgba(255,255,255,0.06)",
      borderColor: state.isFocused
        ? "rgba(0,255,122,0.65)"
        : "rgba(255,255,255,0.16)",
      boxShadow: state.isFocused
        ? "0 0 0 1px rgba(0,255,122,0.55), 0 0 22px rgba(0,255,122,0.18)"
        : "none",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      color: "#E8FDF3",
    }),
    input: (base) => ({ ...base, color: "#E8FDF3" }),
    placeholder: (base) => ({ ...base, color: "rgba(232,253,243,0.6)" }),
    singleValue: (base) => ({ ...base, color: "#E8FDF3" }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "rgba(0,255,122,0.12)",
      border: "1px solid rgba(0,255,122,0.28)",
    }),
    multiValueLabel: (base) => ({ ...base, color: "#E8FDF3" }),
    multiValueRemove: (base) => ({
      ...base,
      color: "rgba(232,253,243,0.8)",
      ":hover": { backgroundColor: "rgba(0,255,122,0.18)", color: "#E8FDF3" },
    }),
  }

  useEffect(() => {
    const loadModel = async () => {
      const loadedClassifier = await pipeline(
        "zero-shot-classification",
        "Xenova/mobilebert-uncased-mnli"
      )
      setClassifier(() => loadedClassifier)
    }

    loadModel()
  }, [])

  const handleKeyDown = (event) => {
    if (!inputValue) return
    switch (event.key) {
      case "Enter":
      case "Tab":
        setClassificationLabels((prev) => [...prev, createOption(inputValue)])
        setInputValue("")
        event.preventDefault()
        break
      default:
        break
    }
  }

  const handleClassify = async () => {
    if (!classifier) {
      alert("Model is still loading, please wait...")
      return
    }

    if (!classificationText || !classificationLabels) {
      alert("Please enter text and labels")
      return
    }

    const labelValues = classificationLabels.map((label) => label.value)
    const output = await classifier(classificationText, labelValues)
    setClassificationResult(output)
  }

  const setExample1 = () => {
    setClassificationText(
      "The restaurant had a cozy atmosphere and the food was delicious, even though the service was a little bit slow."
    )
    setClassificationLabels([
      createOption("positive"),
      createOption("neutral"),
      createOption("negative"),
    ])
  }

  const setRandomExample = () => {
    const randomElement =
      sampleClassificationData[
        Math.floor(Math.random() * sampleClassificationData.length)
      ]
    setClassificationText(randomElement.inputText)
    setClassificationLabels([])
    randomElement.labels.forEach((label) => {
      setClassificationLabels((prev) => [...prev, createOption(label)])
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-6 p-4 text-center">
      <p className="max-w-3xl text-base leading-7 text-base-content/72">
        This page provide zero-shot text classification using a model embedded
        directly in browser. Zero-shot classification allows you to provide an
        input text and a set of candidate categories, and the model will return
        the most likely category for the input text.
        <br />
        Enter the text you want to classify and add labels:
      </p>
      <div className="glass-panel w-full rounded-[28px] p-5 md:p-6">
      <div className="flex w-full flex-col gap-4 lg:flex-row">
        <textarea
          className="textarea textarea-bordered mt-1 min-h-44 w-full bg-white/[0.03]"
          rows={4}
          value={classificationText}
          onChange={(e) => setClassificationText(e.target.value)}
          placeholder="Type your text here..."
        />
        <div className="flex flex-col gap-2 lg:w-72">
          <button className="btn btn-outline btn-primary" onClick={setExample1}>
            Example 1 (sentiment analysis)
          </button>
          <button className="btn btn-outline btn-primary" onClick={setRandomExample}>
            Random Example
          </button>
        </div>
      </div>
      <div className="mt-4 w-full">
        <label className="mb-2 block text-left text-sm font-medium text-base-content/70">
          Labels
        </label>
        <CreatableSelect
          components={components}
          inputValue={inputValue}
          isClearable
          isMulti
          menuIsOpen={false}
          onChange={(newValue) => setClassificationLabels(newValue)}
          onInputChange={(newValue) => setInputValue(newValue)}
          onKeyDown={handleKeyDown}
          placeholder="Type and press enter to add candidate label..."
          value={classificationLabels}
          styles={selectStyles}
          className="w-full"
        />
      </div>
      <div className="mt-5 flex justify-center">
      <button className="btn btn-primary min-w-44" onClick={handleClassify}>
        Classify
      </button>
      </div>
      </div>
      {classificationResult && (
        <div className="glass-panel w-full rounded-[28px] p-5 md:p-6">
          <h2 className="mb-6 text-xl font-bold">Classification Results</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={classificationResult.labels.map((label, index) => ({
                label,
                score: classificationResult.scores[index],
              }))}
              margin={{
                top: 10,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <XAxis dataKey="label" />
              <Tooltip />
              <Bar dataKey="score" fill={NEON_PRIMARY} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

export default TextClassification
